// bm-design-system: delivery ticks primitive
import * as React from "react";
import { Check, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * WhatsApp-style delivery indicator for an outgoing (user) message. Two states:
 *   • "sending" → a single tick   — the message is in flight (optimistic).
 *   • "sent"    → a double tick    — the server has persisted the turn.
 *
 * SSR-safe: a pure function of `state` — no clock, no browser globals — so it
 * renders identically on the server and after hydration. The error case is NOT
 * a tick state; surface it with a Retry affordance next to the message instead.
 */
export type DeliveryState = "sending" | "sent";

const LABELS: Record<DeliveryState, string> = {
  sending: "Mengirim…",
  sent: "Terkirim",
};

export interface DeliveryTicksProps {
  state: DeliveryState;
  className?: string;
}

const DeliveryTicks = React.forwardRef<HTMLSpanElement, DeliveryTicksProps>(
  ({ state, className }, ref) => {
    const Icon = state === "sending" ? Check : CheckCheck;
    return (
      <span
        ref={ref}
        className={cn("delivery-ticks", state === "sent" && "delivery-ticks-sent", className)}
        role="img"
        aria-label={LABELS[state]}
        title={LABELS[state]}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    );
  },
);
DeliveryTicks.displayName = "DeliveryTicks";

export { DeliveryTicks };
